import { useState } from 'react';
import {
  CredentialValidationError,
  fetchAppleSearchAdsOrgs,
  saveCredentials,
  type AppleSearchAdsOrg,
} from '../../api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * Apple Search Ads connect flow.
 *
 * Two steps, because Apple has no redirect OAuth: paste the API certificate,
 * then pick which Campaign Groups (organizations) the audit may read.
 *
 * The advertiser generates an EC P-256 key pair, uploads the PUBLIC half in the
 * Apple Ads UI, and receives clientId / teamId / keyId. The PRIVATE half is
 * pasted here and signs an ES256 JWT server-side. Step 1 exchanges it against
 * Apple to prove it works and to list the orgs — nothing is stored until the
 * user saves, so a bad certificate never reaches the database.
 */

interface Props {
  platformName: string;
  onClose: () => void;
  onConnected: () => void;
}

export default function AppleSearchAdsCredentialModal({
  platformName,
  onClose,
  onConnected,
}: Props) {
  const [clientId, setClientId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [privateKey, setPrivateKey] = useState('');

  const [orgs, setOrgs] = useState<AppleSearchAdsOrg[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const certFilled =
    clientId.trim() && teamId.trim() && keyId.trim() && privateKey.trim();

  const loadOrgs = async () => {
    setBusy(true);
    setError(null);
    try {
      const { orgs: found } = await fetchAppleSearchAdsOrgs({
        client_id: clientId.trim(),
        team_id: teamId.trim(),
        key_id: keyId.trim(),
        private_key: privateKey.trim(),
      });
      setOrgs(found);
      // Default to everything the certificate can read — the common case is
      // "audit all of it", and an empty selection would collect nothing.
      setPicked(new Set(found.map((o) => o.org_id)));
      // A query left over from a previous certificate would filter the new list.
      setSearchQuery('');
    } catch (e) {
      setError(
        e instanceof CredentialValidationError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Could not reach Apple.',
      );
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!orgs) return;
    setBusy(true);
    setError(null);
    try {
      await saveCredentials('apple_search_ads', {
        client_id: clientId.trim(),
        team_id: teamId.trim(),
        key_id: keyId.trim(),
        private_key: privateKey.trim(),
        orgs,
        org_ids: [...picked],
      });
      onConnected();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the certificate.');
      setBusy(false);
    }
  };

  const toggle = (orgId: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  /** Display name for an org — Apple may return `org_name: null`. */
  const orgLabel = (o: AppleSearchAdsOrg) => o.org_name || o.org_id;

  // Name search, matching the id too so the fallback label above stays findable.
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredOrgs = normalizedQuery
    ? (orgs ?? []).filter((o) => orgLabel(o).toLowerCase().includes(normalizedQuery))
    : (orgs ?? []);

  const visibleIds = filteredOrgs.map((o) => o.org_id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => picked.has(id));

  // Scoped to the VISIBLE rows: with a search active, "Select all" must not
  // silently pick up groups the user cannot see.
  const toggleAll = () => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect {platformName}</DialogTitle>
          <DialogDescription>
            {orgs === null
              ? 'Paste your Apple Ads API certificate.'
              : 'Choose which Campaign Groups the audit may read.'}
          </DialogDescription>
        </DialogHeader>
        {/* min-w-0: the dialog lays its children out on a grid, where a child
            defaults to min-width:auto and so cannot shrink below its content.
            A long Campaign Group name is `truncate`d (white-space: nowrap), which
            makes that min-content width the full name — enough to push the whole
            column past the dialog's right edge. */}
        <div className="ba-asa-connect min-w-0 space-y-4">
        {orgs === null ? (
          <>
            <p className="text-sm text-muted-foreground">
              In Apple Ads, go to Account Settings → API and upload your public key.
              Apple gives you the three IDs below; paste the matching private key.
            </p>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="asa-client-id">Client ID</Label>
                <Input id="asa-client-id" value={clientId} autoComplete="off"
                  placeholder="SEARCHADS.a1b2c3d4-…"
                  onChange={(e) => setClientId(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="asa-team-id">Team ID</Label>
                <Input id="asa-team-id" value={teamId} autoComplete="off"
                  placeholder="SEARCHADS.a1b2c3d4-…"
                  onChange={(e) => setTeamId(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="asa-key-id">Key ID</Label>
                <Input id="asa-key-id" value={keyId} autoComplete="off"
                  placeholder="a1b2c3d4-…"
                  onChange={(e) => setKeyId(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="asa-private-key">Private key (.p8 contents)</Label>
                <textarea
                  id="asa-private-key"
                  value={privateKey}
                  onChange={(e) => setPrivateKey(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                  rows={5}
                  placeholder={'-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----'}
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                />
                <p className="text-xs text-muted-foreground">
                  Include the BEGIN and END lines. Both .p8 (PKCS#8) and the
                  <code className="mx-1">openssl ecparam</code> output are accepted.
                </p>
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={loadOrgs} disabled={!certFilled || busy}>
                {busy && <Loader2 className="size-3.5 animate-spin" />}
                {busy ? 'Checking with Apple…' : 'Continue'}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Just the outcome — the DialogDescription above already carries the
                "choose which Campaign Groups" instruction. */}
            <p className="text-sm text-muted-foreground">Certificate accepted.</p>

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                autoComplete="off"
                placeholder="Search Campaign Groups by name"
                aria-label="Search Campaign Groups by name"
                value={searchQuery}
                disabled={busy}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {picked.size} of {orgs.length} selected
              </span>
              <button
                type="button"
                onClick={toggleAll}
                disabled={busy || visibleIds.length === 0}
                className="text-xs underline disabled:opacity-50"
              >
                {allVisibleSelected ? 'Deselect all' : 'Select all'}
              </button>
            </div>

            <div className="h-[min(360px,45vh)] space-y-2 overflow-y-auto pr-1">
              {filteredOrgs.map((o) => {
                const selected = picked.has(o.org_id);
                return (
                  <button
                    key={o.org_id}
                    type="button"
                    onClick={() => toggle(o.org_id)}
                    disabled={busy}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors',
                      selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                      busy && 'opacity-60',
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded border',
                        selected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/40',
                      )}
                    >
                      {selected && (
                        <svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{orgLabel(o)}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {[o.org_id, o.currency, o.time_zone].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </button>
                );
              })}
              {filteredOrgs.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {orgs.length === 0
                    ? 'No Campaign Groups were found for this certificate.'
                    : `No Campaign Groups match “${searchQuery.trim()}”.`}
                </p>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setOrgs(null)} disabled={busy}>
                Back
              </Button>
              <Button onClick={save} disabled={picked.size === 0 || busy}>
                {busy && <Loader2 className="size-3.5 animate-spin" />}
                {busy ? 'Saving…' : 'Connect'}
              </Button>
            </div>
          </>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
